import { useMemo } from 'react';
import useInvoices from './useInvoices';
import useContacts from './useContacts';
import useUsers from './useUsers';
import useExtraPay from './useExtraPay';
import useSubscriptionCharges from './useSubscriptionCharges';
import { useRecurringRevenues } from './useRecurringRevenues';
import { RecurringRevenueStatus } from '../models/recurringRevenue';
import useDepartmentTasks, { DepartmentTeam } from './useDepartmentTasks';
import { Invoice, InvoiceStatus } from '../models/invoice';
import { ContactInfoStatus } from '../models/contactInfo';
import { ExtraPayDocStatus } from '../models/extraPay';

// item 482 — the accountant's total OUTSTANDING (actionable) count for the sidebar badge.
// item 1269 (Chad reconciled the badge against the desk: "I count 56, it says 71") — the old
// formula summed OVERLAPPING queues (to-enter + awaiting-deposit + open AR), so one invoice
// could tick the badge three times. Now it counts DESK ROWS exactly the way the ledgers show
// them: Enter-to-QB rows (invoices to enter) + Deposits rows still on the desk (undeposited
// invoices — awaiting payment or awaiting the deposit confirm — plus undeposited subscription
// charges) + new contacts/users to enter + approved extra pay + open accounting tasks.
// History rows never count. Reuses the app-wide cached queries (react-query dedupes).
const useAccountantOutstanding = () => {
  const { data: invData, status: invStatus } = useInvoices();
  const { data: contactsData } = useContacts();
  const { data: usersData } = useUsers();
  const { charges } = useSubscriptionCharges();
  const { data: recurringData } = useRecurringRevenues();
  const { docs } = useExtraPay();
  const { tasks } = useDepartmentTasks();

  return useMemo(() => {
    const invoices = (invData?.index ?? []) as Invoice[];
    // the Enter-to-QB invoices bucket's actionable rows
    const toEnter = invoices.filter((i) => i.isFinalized && !i.isVoid && i.status === InvoiceStatus.New).length;
    // item 1273 (Chad: "we'll never have a zero balance on our AR — remove that from the green
    // checkbox") — AWAITING-PAYMENT rows are the customer's move, not the accountant's: they no
    // longer tick the badge. What still counts from Deposits is money ALREADY IN awaiting the
    // QB deposit confirm (stage PAID) — that IS QuickBooks entry work, and it reaches zero.
    const depositInvoiceRows = invoices.filter((i) => i.isFinalized && !i.isVoid
      && i.status !== InvoiceStatus.DoNotEnter && i.status !== InvoiceStatus.WrittenOff
      && (i.status === InvoiceStatus.New || i.status === InvoiceStatus.NewEntered)
      && ((i.stripePaymentStatus ?? 0) !== 0 || !!i.paidDate)).length;
    const depositChargeRows = charges.filter((c) => !c.isDeposited
      && (c.stripePaid || !!c.paidDate)).length;
    // item 1275 (Chad: "subs recognizing enrolled/canceled is in the NOT green checkbox but
    // not calculating in the nag") — the SUBSCRIPTIONS hierarchy bucket's actionable rows,
    // EXACTLY the ledger's rule (item 1233): a live sub not yet recognized in QB, or a
    // cancelled sub awaiting the cancel acknowledgment.
    const subHierarchyRows = ((recurringData?.index ?? []) as any[])
      .filter((r) => !r.isVoid)
      .filter((r) => (r.status === RecurringRevenueStatus.Cancelled
        ? !r.qbCancelAcknowledgedDate
        : !r.qbEnteredDate)).length;
    const newContacts = (contactsData?.index ?? []).filter((c) => c.status === ContactInfoStatus.New).length;
    const newUsers = (usersData?.index ?? []).filter((u) => !u.accountingEnteredDate).length;
    const extraPayApproved = docs.filter((d) => d.status === ExtraPayDocStatus.Approved).length;
    const openAcctTasks = tasks.filter((t) => t.department === DepartmentTeam.Accounting && !t.isDone).length;

    const total = toEnter + subHierarchyRows + depositInvoiceRows + depositChargeRows
      + newContacts + newUsers + extraPayApproved + openAcctTasks;
    const loaded = invStatus === 'success';
    return { total, caughtUp: loaded && total === 0, loaded };
  }, [invData, contactsData, usersData, charges, recurringData, docs, tasks, invStatus]);
};

export default useAccountantOutstanding;
